import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Change, Project, ProjectLabel } from '../../models';
import { Icon, Button } from '..';
import { RootState, updateProject, useAppDispatch, useAppSelector } from '../../redux';

interface ChangeLabelsFieldProps {
    change: Change;
    project: Project;
    readOnly?: boolean;
    onSave: (field: string, value: string[]) => void | Promise<void>;
}

const ChangeLabelsField = (props: ChangeLabelsFieldProps) => {
    const { change, project: projectProp, readOnly, onSave } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const rootRef = useRef<HTMLDivElement>(null);
    const { project: projectFromStore } = useAppSelector((state: RootState) => state.projects);
    const { change: changeFromStore } = useAppSelector((state: RootState) => state.changes);

    // Prefer live Redux state so create/attach reflects immediately in the modal.
    const project = (projectFromStore?._id === projectProp?._id ? projectFromStore : projectProp) || projectProp;
    const liveChange = (changeFromStore?._id === change?._id ? changeFromStore : change) || change;

    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [newColor, setNewColor] = useState(ProjectLabel.DEFAULT_COLORS[5]);
    const [saving, setSaving] = useState(false);

    const labels = project?.labels || [];
    const selectedIds = liveChange?.labelIds || [];

    const selectedLabels = useMemo(() => {
        return labels.filter((label: ProjectLabel) => selectedIds.includes(label._id));
    }, [labels, selectedIds]);

    useEffect(() => {
        if (!open)
            return;
        const onDocClick = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node))
                setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const toggleLabel = async (labelId: string) => {
        if (readOnly)
            return;
        const next = selectedIds.includes(labelId)
            ? selectedIds.filter((id: string) => id !== labelId)
            : [...selectedIds, labelId];
        await onSave('labelIds', next);
    };

    const createAndAttach = async () => {
        const name = newName.trim();
        if (!name || saving)
            return;

        setSaving(true);
        try {
            const label = new ProjectLabel({ _id: uuidv4(), name, color: newColor });
            const nextLabels = [
                ...labels.map((l: ProjectLabel) => ({ _id: l._id, name: l.name, color: l.color })),
                { _id: label._id, name: label.name, color: label.color },
            ];
            await dispatch(updateProject({
                ...project,
                labels: nextLabels,
            }));
            await onSave('labelIds', [...selectedIds, label._id]);
            setNewName('');
            setCreating(false);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div ref={rootRef} className="relative ml-auto flex items-center justify-end min-w-0 max-w-[55%]">
            <button
                type="button"
                className="flex items-center gap-1 min-w-0 max-w-full rounded px-1 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800"
                onClick={() => !readOnly && setOpen(!open)}
                title={t('changes.labels') || ''}
                disabled={readOnly && selectedLabels.length === 0}
            >
                {selectedLabels.length > 0 ? (
                    <span className="flex items-center gap-1 min-w-0 overflow-hidden">
                        {selectedLabels.map((label: ProjectLabel) => (
                            <span
                                key={label._id}
                                className="inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium leading-tight max-w-[6rem] truncate"
                                style={{
                                    backgroundColor: label.color,
                                    color: ProjectLabel.textColor(label.color),
                                }}
                            >
                                <Icon type="tag" size={3} className="shrink-0" color="inherit" />
                                <span className="truncate">{label.name}</span>
                            </span>
                        ))}
                    </span>
                ) : (
                    <span className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        <Icon type="tag" size={3} className="mr-1" />
                        {t('changes.labels')}
                    </span>
                )}
                {!readOnly && (
                    <Icon type="down" size={3} className="shrink-0 text-gray-400" />
                )}
            </button>

            {open && !readOnly && (
                <div className="absolute right-0 top-full z-50 mt-1 w-64 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 shadow-lg">
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {t('changes.labels')}
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-0.5">
                        {labels.length === 0 && !creating && (
                            <div className="text-xs italic text-gray-400 px-1 py-1">
                                {t('changes.labels_empty')}
                            </div>
                        )}
                        {labels.map((label: ProjectLabel) => {
                            const active = selectedIds.includes(label._id);
                            return (
                                <button
                                    key={label._id}
                                    type="button"
                                    className={`flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-900 ${active ? 'bg-cyan-50 dark:bg-cyan-950/30' : ''}`}
                                    onClick={() => toggleLabel(label._id)}
                                >
                                    <span
                                        className="inline-block w-3 h-3 rounded-full shrink-0"
                                        style={{ backgroundColor: label.color }}
                                    />
                                    <span className="flex-1 truncate">{label.name}</span>
                                    {active && <Icon type="check" size={3} className="text-cyan-700" />}
                                </button>
                            );
                        })}
                    </div>

                    {!creating ? (
                        <button
                            type="button"
                            className="mt-1.5 text-xs text-cyan-700 hover:underline"
                            onClick={() => setCreating(true)}
                        >
                            <Icon type="plus" size={3} className="inline mr-1" />
                            {t('changes.labels_create')}
                        </button>
                    ) : (
                        <div className="mt-2 flex flex-col gap-1.5 border-t pt-2">
                            <input
                                type="text"
                                className="block w-full rounded-md border-0 py-1 px-2 text-xs text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600"
                                placeholder={t('changes.labels_name_placeholder') || ''}
                                value={newName}
                                autoFocus
                                onChange={(e) => setNewName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        createAndAttach();
                                    }
                                }}
                            />
                            <div className="flex flex-wrap gap-1">
                                {ProjectLabel.DEFAULT_COLORS.map((color: string) => (
                                    <button
                                        key={color}
                                        type="button"
                                        className={`w-4 h-4 rounded-full border ${newColor === color ? 'ring-2 ring-offset-1 ring-cyan-600' : 'border-gray-200 dark:border-gray-700'}`}
                                        style={{ backgroundColor: color }}
                                        onClick={() => setNewColor(color)}
                                        aria-label={color}
                                    />
                                ))}
                            </div>
                            <div className="flex gap-1 justify-end">
                                <Button
                                    title={t('common.cancel') || ''}
                                    color="none"
                                    small
                                    onClick={() => { setCreating(false); setNewName(''); }}
                                />
                                <Button
                                    title={t('common.add') || ''}
                                    color="secondary"
                                    icon="plus"
                                    small
                                    loading={saving}
                                    onClick={createAndAttach}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ChangeLabelsField;
